import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createCoinIcon } from '../render/coin';
import { simpleBalance, compoundBalance, effectiveAnnualRate } from '../finance/interest';
import { yearlySeries, type Series } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface CompoundInterestState {
  principal: number; // P
  rate: number; // r, decimal
  years: number; // t
  frequency: number; // n per year (advanced; foundation pins n = 1)
  showSimple: boolean;
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

export function compoundInterestModel(state: CompoundInterestState): {
  series: Series[];
  readout: ReadoutRow[];
} {
  const { principal: P, rate: r, years, frequency: n, showSimple } = state;

  const compound = yearlySeries('compound', (t) => compoundBalance(P, r, t, n), years);
  const series: Series[] = [compound];

  const compoundFinal = compound.points.at(-1)?.value ?? P;

  let simpleFinal = P;
  if (showSimple) {
    const simple = yearlySeries('simple', (t) => simpleBalance(P, r, t), years);
    series.push(simple);
    simpleFinal = simple.points.at(-1)?.value ?? P;
  }

  const gap = compoundFinal - simpleFinal;
  const apy = effectiveAnnualRate(r, n);

  const readout: ReadoutRow[] = [
    {
      id: 'final-compound',
      lhs: 'stat.final-compound.label',
      relation: '=',
      rhs: formatMoney(compoundFinal),
    },
  ];

  if (showSimple) {
    readout.push({
      id: 'final-simple',
      lhs: 'stat.final-simple.label',
      relation: '=',
      rhs: formatMoney(simpleFinal),
    });
    readout.push({
      id: 'gap',
      lhs: 'stat.gap.label',
      relation: '=',
      rhs: formatMoney(gap),
      emphasis: gap > 0 ? 'invariant' : 'neutral',
    });
  }

  // Advanced: show APY vs nominal
  if (n !== 1) {
    readout.push({
      id: 'apy',
      lhs: 'stat.apy.label',
      relation: '≈',
      rhs: formatPercent(apy, 2),
    });
    readout.push({
      id: 'nominal',
      lhs: 'stat.nominal.label',
      relation: '=',
      rhs: formatPercent(r, 2),
    });
  }

  return { series, readout };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(advanced: boolean): CompoundInterestState {
  return {
    principal: 1000,
    rate: 0.07,
    years: 20,
    frequency: advanced ? 12 : 1,
    showSimple: true,
  };
}

const PRESETS = [
  { id: 'starter', labelKey: 'preset.starter.label' as FinanceKey },
  { id: 'decades', labelKey: 'preset.decades.label' as FinanceKey },
  { id: 'high-rate', labelKey: 'preset.high-rate.label' as FinanceKey },
];

function applyPresetLocal(state: CompoundInterestState, id: string): CompoundInterestState {
  switch (id) {
    case 'starter':
      return { ...state, principal: 1000, rate: 0.05, years: 10, showSimple: true };
    case 'decades':
      return { ...state, principal: 5000, rate: 0.07, years: 40, showSimple: true };
    case 'high-rate':
      return { ...state, principal: 2000, rate: 0.15, years: 15, showSimple: true };
    default:
      return state;
  }
}

function randomise(
  state: CompoundInterestState,
  prng: (max?: number) => number,
): CompoundInterestState {
  const principals = [500, 1000, 2000, 5000, 10000];
  const rates = [0.03, 0.05, 0.07, 0.08, 0.1, 0.12];
  const yearOptions = [5, 10, 15, 20, 25, 30];
  return {
    ...state,
    principal: principals[Math.floor(prng() * principals.length)] ?? 1000,
    rate: rates[Math.floor(prng() * rates.length)] ?? 0.07,
    years: yearOptions[Math.floor(prng() * yearOptions.length)] ?? 20,
  };
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<CompoundInterestState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.compound-interest.label',
    demoId: 'finance',
    advancedKey: 'compound-interest',

    initialState: (ctx) => defaultState(ctx.advanced),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, CompoundInterestState>[] {
      const rows: PanelRow<FinanceKey, CompoundInterestState>[] = [
        {
          kind: 'slider',
          id: 'principal',
          labelKey: 'param.principal.label',
          min: 100,
          max: 50000,
          step: 100,
          value: state.principal,
          format: formatMoney,
          onChange: (v) => ({ ...state, principal: v }),
        },
        {
          kind: 'slider',
          id: 'rate',
          labelKey: 'param.rate.label',
          min: 0.01,
          max: 0.25,
          step: 0.005,
          value: state.rate,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, rate: v }),
        },
        {
          kind: 'slider',
          id: 'years',
          labelKey: 'param.years.label',
          min: 1,
          max: 50,
          step: 1,
          value: state.years,
          format: formatYears,
          onChange: (v) => ({ ...state, years: v }),
        },
        {
          kind: 'toggle',
          labelKey: 'param.showSimple.label',
          value: state.showSimple,
          onChange: (v) => ({ ...state, showSimple: v }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(4, 0, {
          kind: 'dropdown',
          id: 'frequency',
          labelKey: 'param.frequency.label',
          options: [
            { value: '1', labelKey: 'param.frequency.annual' as FinanceKey },
            { value: '2', labelKey: 'param.frequency.biannual' as FinanceKey },
            { value: '4', labelKey: 'param.frequency.quarterly' as FinanceKey },
            { value: '12', labelKey: 'param.frequency.monthly' as FinanceKey },
            { value: '365', labelKey: 'param.frequency.daily' as FinanceKey },
          ],
          value: String(state.frequency),
          onChange: (v) => ({ ...state, frequency: parseInt(v, 10) }),
        });
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, CompoundInterestState> {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): CompoundInterestState {
      return {
        ...state,
        years: 30,
        showSimple: true,
        frequency: 12,
      };
    },

    createRender(ctx, host, _controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Add coin icon at the y-axis head (top-left of plot area)
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Line series layer
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group (appended to svg, above plot)
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      function updateLegend(state: CompoundInterestState): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        // Right-anchor labels so they never overflow the plot area.
        const labelRightX = width - padding.right - 8;
        const swatchLen = 16;
        const swatchGap = 6;
        const legendY = padding.top + 16;

        const items = [
          { cls: 'fin-line-compound fin-legend-dot', key: 'series.compound.label' as FinanceKey },
          ...(state.showSimple
            ? [{ cls: 'fin-line-simple fin-legend-dot', key: 'series.simple.label' as FinanceKey }]
            : []),
        ];

        items.forEach((item, i) => {
          const rowY = legendY + i * 18;
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          const lineEl = createSvgEl('line', {
            class: item.cls,
            x1: swatchX1,
            y1: rowY,
            x2: swatchX2,
            y2: rowY,
          });
          legendGroup.appendChild(lineEl);

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = ctx.i18n.t(item.key);
          legendGroup.appendChild(label);
        });
      }

      return {
        update(state: CompoundInterestState): void {
          const m = compoundInterestModel(state);
          const compound = m.series.find((s) => s.id === 'compound');
          const compoundFinal = compound?.points.at(-1)?.value ?? state.principal;

          chart.setScales({
            xDomain: [0, state.years],
            yDomain: [0, niceCeil(compoundFinal * 1.05)],
          });

          // Draw compound series
          const compoundSeries = m.series.find((s) => s.id === 'compound');
          if (compoundSeries) {
            lines.setSeries('compound', compoundSeries.points, 'fin-line-compound');
          }

          // Draw or clear simple series
          const simpleSeries = m.series.find((s) => s.id === 'simple');
          if (simpleSeries) {
            lines.setSeries('simple', simpleSeries.points, 'fin-line-simple');
          } else {
            lines.clear('simple');
          }

          updateLegend(state);
          readout.update(m.readout.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
